import {
  BadRequest,
  InternalError,
  NetworkError,
  ServiceUnavailable,
} from 'common/errors'
import { useEffect, useState } from 'react'
import { BaseError } from 'src/editor/api/errors/base-error'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { getUserData } from '../api/user-data-api'
import {
  getGoogleRegistrationAuthUrl,
  googleRegistrationAuth,
} from '../api/website-public-page-api'

export const getGoogleRegistrationAuthPayload = async (code: string) => {
  try {
    const response = await getUserData()
    const locale = response.data?.dashboardLocale

    if (locale) {
      return { code, locale }
    }
  } catch {
    // Locale lookup is optional: continue with code-only payload.
  }

  return { code }
}

export const useGoogleLoginSubmit = () => {
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  const { t } = useTypedTranslation()

  const handleGoogleSignIn = async () => {
    setErrors([])
    setLoading(true)
    try {
      const response = await getGoogleRegistrationAuthUrl()
      window.location.href = response.data?.authUrl
    } catch (error) {
      if (error instanceof BaseError) {
        setErrors([error.response.detail])
      } else if (error instanceof ServiceUnavailable) {
        setErrors([
          error.response.data.reason ||
            error.response.data.message ||
            t('core.error.title'),
        ])
      } else if (error instanceof BadRequest) {
        setErrors(error.response.data.errors.common)
      } else if (error instanceof NetworkError) {
        setErrors([t('core.errors.no_connection')])
      } else if (error instanceof InternalError) {
        setErrors([t('core.error.title')])
      }
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleCodeAuth = async (code: string) => {
    setErrors([])
    setLoading(true)
    try {
      const payload = await getGoogleRegistrationAuthPayload(code)
      const response = await googleRegistrationAuth(payload)
      const redirect = response.data?.nextStepUrl
      window.location.replace(redirect)
    } catch (error) {
      if (error instanceof BaseError) {
        setErrors([error.response.detail])
      } else if (error instanceof ServiceUnavailable) {
        setErrors([
          error.response.data.reason ||
            error.response.data.message ||
            t('core.error.title'),
        ])
      } else if (error instanceof BadRequest) {
        setErrors(error.response.data.errors.common)
      } else if (error instanceof NetworkError) {
        setErrors([t('core.errors.no_connection')])
      } else if (error instanceof InternalError) {
        setErrors([t('core.error.title')])
      } else {
        setErrors([t('core.error.title')])
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    if (!code) {
      return
    }
    handleGoogleCodeAuth(code)
  }, [])

  return {
    loading,
    errors: errors,
    handleGoogleSignIn,
  }
}
